import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { FetchReturn, IProductCategory } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetProductCategories = (filters: string[] = []) => {
  return useQuery({
    queryKey: queryKeys.productCategories.all(filters),
    queryFn: () => fetchGetProductCategories(filters),
    staleTime: Infinity,
  });
};

export const useGetProductCategoriesWithPagination = (
  filters: string[] = [],
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.productCategories.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchGetProductCategories(concat(filters, [`page=${pageParam}`]), true),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

export const fetchGetProductCategories = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IProductCategory[]>> => {
  const url = `/productCategories${generateQuery(
    [],
    concat(filters, isPagination ? '' : 'pageSize=0'),
  )}`;

  const response = await requestCCApi({ url });

  if (!isPagination) {
    return response.data as FetchReturn<T, IProductCategory[]>;
  } else {
    return {
      data: response.data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IProductCategory[]>;
  }
};
